import { bsClassJustifyContent } from '@common/types/bootstrap';
import classNames from 'classnames';
import { isEmpty } from 'lodash';
import React, { PropsWithChildren } from 'react';
import { Form, Row, Col } from 'react-bootstrap';
import { LabelPosition } from "@common/models/Enums/LabelPosition";

interface IFormGroupProps {
    controlId: string,
    controlLabel: string,
    reverseLabel?: boolean,
    controlLabelRowJustifyClass?: bsClassJustifyContent,
    controlLabelTailElement?: JSX.Element,
    helpId?: string,
    helpText?: string
    errorId: string
    errorMessage?: string
    className?: string
    labelPosition?: LabelPosition
    /**
     * Based on the BootStrap grid system, this value define the
     * width of the the column where the label will be positioned 
     * on small breackpoint.
     * The value will be used when the LabelPosition is defined
     * as LabelPosition.LEFT.
     */
    labelColumnSmWidth?: number;
    /**
     * Based on the BootStrap grid system, this value define the
     * width of the the column where the input will be positioned
     * on small breackpoint.
     * The value will be used when the LabelPosition is defined
     * as LabelPosition.LEFT.
     */
    inputColumnSmWidth?: number;
    /**
     * Based on the BootStrap grid system, this value define the
     * width of the the column where the label will be positioned 
     * on medium breackpoint.
     * The value will be used when the LabelPosition is defined
     * as LabelPosition.LEFT.
     */
    labelColumnMdWidth?: number;
    /**
     * Based on the BootStrap grid system, this value define the
     * width of the the column where the input will be positioned
     * on medium breackpoint.
     * The value will be used when the LabelPosition is defined
     * as LabelPosition.LEFT.
     */
    inputColumnMdWidth?: number;
    /**
     * Class(es) name(s) for the column where the input will be placed.
     */
    inputColumClassName?: string;
}

const RenderLabel = (controlId: string, label: string, justifyClass: bsClassJustifyContent, labelPosition: LabelPosition,
    labelColumnSmWidth?: number, labelColumnMdWidth?: number, controlLabelTailElement?: JSX.Element): JSX.Element => {
    if (controlLabelTailElement) {
        return (
            <div className={classNames("d-flex flex-row", justifyClass)}>
                {!isEmpty(label) && <Form.Label htmlFor={controlId}>{label}</Form.Label>}
                {controlLabelTailElement}
            </div>
        )
    }
    else if (isEmpty(label)) {
        return (<></>)
    }
    else {
        return RenderLabelPosition(controlId, label, labelPosition, labelColumnSmWidth, labelColumnMdWidth)
    }
}

const RenderLabelPosition = (controlId: string, label: string, labelPosition: LabelPosition,
    labelColumnSmWidth?: number, labelColumnMdWidth?: number): JSX.Element => {

    let labelToRender = <></>

    switch (labelPosition) {

        case LabelPosition.TOP:
            labelToRender = <Form.Label htmlFor={controlId}>{label}</Form.Label>
            break;

        case LabelPosition.LEFT:
            labelToRender = <Col sm={labelColumnSmWidth} md={labelColumnMdWidth} className='d-flex align-items-center'>
                <Form.Label htmlFor={controlId}>{label}</Form.Label>
            </Col>
            break;
    }

    return labelToRender

}

export const FormGroup = ({ controlLabelRowJustifyClass = "justify-content-between", ...props }: PropsWithChildren<IFormGroupProps>) => {

    const {
        controlId,
        controlLabel,
        labelPosition = LabelPosition.TOP,
        controlLabelTailElement,
        labelColumnSmWidth = 6,
        inputColumnSmWidth = 6,
        labelColumnMdWidth = 6,
        inputColumnMdWidth = 6,
        inputColumClassName = ""
    } = props
    let formGroup

    switch (labelPosition) {

        case LabelPosition.LEFT:
            formGroup = <Form.Group data-testid={props.controlId} className={props.className}>
                <Row className='mt-3'>
                    {RenderLabel(controlId, controlLabel, controlLabelRowJustifyClass, labelPosition as LabelPosition,
                        labelColumnSmWidth, labelColumnMdWidth, controlLabelTailElement)}
                    <Col className={inputColumClassName} sm={inputColumnSmWidth} md={inputColumnMdWidth}>
                        {props.children}
                    </Col>
                </Row>
            </Form.Group>
            break;

        case LabelPosition.TOP:
            formGroup = <Form.Group data-testid={props.controlId} className={props.className}>
                {!props.reverseLabel && RenderLabel(
                    props.controlId,
                    props.controlLabel,
                    controlLabelRowJustifyClass,
                    labelPosition as LabelPosition,
                    labelColumnSmWidth,
                    labelColumnMdWidth,
                    props.controlLabelTailElement
                )}
                {props.children}
                {
                    props.reverseLabel &&
                    RenderLabel(props.controlId, props.controlLabel, controlLabelRowJustifyClass, labelPosition as LabelPosition,
                        labelColumnSmWidth, labelColumnMdWidth, props.controlLabelTailElement)}
                {props.helpText && <Form.Text id={props.helpId}>{props.helpText}</Form.Text>}
                <Form.Control.Feedback type="invalid">
                    {props.errorMessage && <span role="alert" id={props.errorId}>{props.errorMessage}</span>}
                </Form.Control.Feedback>
            </Form.Group>
            break;
    }

    return formGroup
}
